import { useEffect, useState, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { authFetch } from "../utils/api";
import {
  isAdminOrOwner,
  getRole,
  setRole,
  setCurrentOrgId,
  getCurrentOrgId,
  clearWorkspaceScopedSelections,
} from "../utils/permissions";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  Modal,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faPen,
  faPlus,
  faXmark,
  faRightFromBracket,
  faCircleExclamation,
  faCircleCheck,
  faCircleInfo,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "./sectionPages.css";
import "./Team.css";
import "../components/confirmationModal.css";
import ConfirmationModal from "../components/confirmationModal";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const INVITE_ROLES = [
  { value: "read", labelKey: "team_role_read" },
  { value: "write", labelKey: "team_role_write" },
  { value: "admin", labelKey: "team_role_admin" },
];

// Shared sx for outlined MUI inputs (matches Administration.js theme).
const inputSx = {
  "& .MuiOutlinedInput-root.Mui-focused": {
    "& fieldset": { border: "1px solid #515151" },
  },
  "& .MuiInputLabel-root": {
    color: "gray",
    "&.Mui-focused": { color: "#5616ea" },
  },
};

// Mirrors the smooth hover used by `.delete-btn` in `bots.css` so all
// destructive/edit icon buttons across the portal feel the same.
const actionBtnSx = {
  padding: "5px 10px",
  fontSize: 16,
  borderRadius: "5px",
  border: "1px solid #ddd",
  backgroundColor: "#f9f9f9",
  color: "#474747",
  transition:
    "background-color 0.5s ease, color 0.3s ease, transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.3s ease",
  willChange: "transform",
  backfaceVisibility: "hidden",
  "&:hover": {
    backgroundColor: "#ededed",
    transform: "scale(1.1)",
  },
  "&.Mui-disabled": {
    backgroundColor: "#ededed",
    color: "#9ca3af",
    border: "1px solid #ddd",
    transform: "scale(1.0)",
    cursor: "not-allowed",
  },
};

// Brand-purple plus button used in card headers, sharing the same
// smooth bots.css-style transition as the action icons above. Slightly
// wider than tall with an 8px corner radius to match the rest of the
// app's button styling instead of looking like a circular pill.
const plusBtnSx = {
  color: "#fff",
  backgroundColor: "#5616ea",
  height: 36,
  borderRadius: "8px",
  paddingLeft: "14px",
  paddingRight: "14px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  whiteSpace: "nowrap",
  transition:
    "background-color 0.5s ease, transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  willChange: "transform",
  backfaceVisibility: "hidden",
  "&:hover": {
    backgroundColor: "#4711c4",
    transform: "scale(1.05)",
  },
  "&.Mui-disabled": {
    backgroundColor: "#c7b8f5",
    color: "#fff",
    transform: "scale(1.0)",
    cursor: "not-allowed",
  },
};

// Renders one account-level section, chosen by `section`:
//   "workspaces" → the Workspaces page (/workspaces)
//   "members"    → the Team members page (/team-members)
// Each is its own sidebar entry / route now, so there are no in-page tabs.
const Team = ({ section = "workspaces" } = {}) => {
  const { t } = useTranslation();
  const role = getRole();
  const adminOrOwner = isAdminOrOwner();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const activeTab = section;

  // Org metadata
  const [currentOrg, setCurrentOrg] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [busy, setBusy] = useState(false);

  // Members
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  // Top-level feedback
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("read");
  const [inviting, setInviting] = useState(false);

  // Inline workspace create / rename
  // editingId === "new"        → create row
  // editingId === <number>     → renaming an existing workspace
  // editingId === null         → no inline editor open
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Workspace selected for the "switch workspace" confirmation modal.
  const [switchTarget, setSwitchTarget] = useState(null);

  // Standalone modals for create-workspace and invite-member flows.
  const [showCreateWs, setShowCreateWs] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  // Workspace currently being renamed via the edit-workspace modal.
  const [editTarget, setEditTarget] = useState(null);
  // Member-role edit modal target (null = closed). Holds the member
  // row we're editing; the chosen role lives in `editMemberRole`.
  const [editMember, setEditMember] = useState(null);
  const [editMemberRole, setEditMemberRole] = useState("read");

  // Generic destructive-action confirmation. When set, the shared
  // ConfirmationModal opens and calls `action()` on confirm.
  // Shape: { itemName, action, requireTextConfirmation? }
  const [confirmTarget, setConfirmTarget] = useState(null);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3500);
  };

  // -----------------------------------------------------------------
  const fetchOrgs = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}orgs`);
      if (!res || !res.ok) return;
      const data = await res.json();
      setOrganizations(data.organizations || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const fetchCurrent = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}orgs/current`);
      if (!res || !res.ok) return;
      const data = await res.json();
      setCurrentOrg(data);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}orgs/current/members`);
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("team_load_members_failed"));
      }
      const data = await res.json();
      setMembers(data.members || []);
      setPending(data.pending_invitations || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always resolve the current workspace (used in both section headers).
    fetchCurrent();
    if (section === "members") {
      if (adminOrOwner) fetchMembers();
      else setLoading(false);
    } else {
      fetchOrgs();
      setLoading(false);
    }
  }, [section, adminOrOwner, fetchOrgs, fetchCurrent, fetchMembers]);

  // -----------------------------------------------------------------
  // Workspace actions
  // -----------------------------------------------------------------
  const handleSwitchOrg = async (orgId) => {
    if (orgId === getCurrentOrgId() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}orgs/switch`, {
        method: "POST",
        body: JSON.stringify({ organization_id: orgId }),
      });
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("team_switch_failed"));
      }
      const data = await res.json();
      localStorage.setItem("token", data.token);
      setRole(data.role, data.owner_user_id);
      setCurrentOrgId(data.current_org_id);
      // Drop stale selectedBotId/Ids from the previous workspace so
      // pages like Analytics/Inbox/Leads don't immediately 403.
      clearWorkspaceScopedSelections();
      // Stay on the current page (e.g. /team-members or /workspaces) so the
      // user lands back where they were instead of being kicked to /my-bots.
      window.location.reload();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = editValue.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}orgs`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("team_create_failed"));
      }
      const data = await res.json();
      setEditingId(null);
      setEditValue("");
      setShowCreateWs(false);
      // Switch into the freshly-created workspace.
      await handleSwitchOrg(data.id);
    } catch (e2) {
      setError(e2.message);
      setBusy(false);
    }
  };

  const handleRename = async (orgId, e) => {
    e?.preventDefault?.();
    const name = editValue.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}orgs/${orgId}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("team_rename_failed"));
      }
      setOrganizations((prev) =>
        prev.map((o) => (o.id === orgId ? { ...o, name } : o))
      );
      if (currentOrg && currentOrg.id === orgId) {
        setCurrentOrg({ ...currentOrg, name });
      }
      showSuccess(t("team_workspace_renamed"));
      setEditingId(null);
      setEditValue("");
      setEditTarget(null);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = (org) => {
    if (org.is_personal) return;
    setConfirmTarget({
      itemName: t("team_confirm_delete_workspace", { name: org.name }),
      requireTextConfirmation: true,
      action: async () => {
        setBusy(true);
        setError(null);
        try {
          const res = await authFetch(`${API_BASE}orgs/${org.id}`, {
            method: "DELETE",
          });
          if (!res || !res.ok) {
            const data = res ? await res.json().catch(() => ({})) : {};
            throw new Error(data.detail || t("team_delete_failed"));
          }
          const data = await res.json().catch(() => ({}));
          if (data.token) localStorage.setItem("token", data.token);
          if (data.current_org_id) setCurrentOrgId(data.current_org_id);
          window.location.href = "/my-bots";
        } catch (e2) {
          setError(e2.message);
          setBusy(false);
        }
      },
    });
  };

  const handleLeave = (org) => {
    setConfirmTarget({
      itemName: t("team_confirm_leave_workspace", { name: org.name }),
      action: async () => {
        const myUserId = Number(localStorage.getItem("user_id"));
        setBusy(true);
        setError(null);
        try {
          // Switch into the workspace we want to leave so the
          // members-by-current-org endpoint works, then delete ourselves.
          if (org.id !== getCurrentOrgId()) {
            const sw = await authFetch(`${API_BASE}orgs/switch`, {
              method: "POST",
              body: JSON.stringify({ organization_id: org.id }),
            });
            if (!sw || !sw.ok) {
              const data = sw ? await sw.json().catch(() => ({})) : {};
              throw new Error(data.detail || t("team_switch_failed"));
            }
            const swData = await sw.json();
            localStorage.setItem("token", swData.token);
            setCurrentOrgId(swData.current_org_id);
          }
          const res = await authFetch(
            `${API_BASE}orgs/current/members/${myUserId}`,
            { method: "DELETE" }
          );
          if (!res || !res.ok) {
            const data = res ? await res.json().catch(() => ({})) : {};
            throw new Error(data.detail || t("team_leave_failed"));
          }
          // Land back on the Workspaces page so the user sees the
          // updated list (and is now in their personal workspace).
          clearWorkspaceScopedSelections();
          window.location.href = "/workspaces";
        } catch (e) {
          setError(e.message);
          setBusy(false);
        }
      },
    });
  };

  // -----------------------------------------------------------------
  // Member actions
  // -----------------------------------------------------------------
  const handleInvite = async (e) => {
    e?.preventDefault?.();
    setInviting(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}orgs/current/invitations`, {
        method: "POST",
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole,
        }),
      });
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("team_invite_failed"));
      }
      showSuccess(t("team_invitation_sent", { email: inviteEmail.trim() }));
      setInviteEmail("");
      setInviteRole("read");
      setShowInvite(false);
      await fetchMembers();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberUserId, newRole) => {
    setError(null);
    setBusy(true);
    try {
      const res = await authFetch(
        `${API_BASE}orgs/current/members/${memberUserId}`,
        { method: "PUT", body: JSON.stringify({ role: newRole }) }
      );
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("team_role_update_failed"));
      }
      showSuccess(t("team_role_updated"));
      setEditMember(null);
      await fetchMembers();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveMember = (memberUserId, email) => {
    setConfirmTarget({
      itemName: email,
      action: async () => {
        setError(null);
        try {
          const res = await authFetch(
            `${API_BASE}orgs/current/members/${memberUserId}`,
            { method: "DELETE" }
          );
          if (!res || !res.ok) {
            const data = res ? await res.json().catch(() => ({})) : {};
            throw new Error(data.detail || t("team_remove_member_failed"));
          }
          showSuccess(t("team_member_removed"));
          await fetchMembers();
        } catch (e) {
          setError(e.message);
        }
      },
    });
  };

  const handleRevokeInvite = (invitationId, email) => {
    setConfirmTarget({
      itemName: t("team_confirm_revoke_invite", { email }),
      action: async () => {
        setError(null);
        try {
          const res = await authFetch(
            `${API_BASE}orgs/current/invitations/${invitationId}`,
            { method: "DELETE" }
          );
          if (!res || !res.ok) {
            const data = res ? await res.json().catch(() => ({})) : {};
            throw new Error(data.detail || t("team_revoke_failed"));
          }
          showSuccess(t("team_invitation_revoked"));
          await fetchMembers();
        } catch (e) {
          setError(e.message);
        }
      },
    });
  };

  // -----------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------
  const orgName = currentOrg?.name || t("team_default_workspace_name");
  const myUserId = Number(localStorage.getItem("user_id"));

  const renderWorkspaceRow = (o) => {
    const isCurrent = o.id === getCurrentOrgId();
    const canRename = o.role === "owner" || o.role === "admin";
    const canDelete = o.role === "owner" && !o.is_personal;
    // Anyone who isn't the owner can leave; the owner is blocked
    // server-side by the "last owner" check (and the personal
    // workspace's owner is always its only owner).
    const canLeave = o.role !== "owner";

    // Stop row-level click-to-switch when clicking inside the actions cluster.
    const stop = (e) => e.stopPropagation();
    const rowClickable = !isCurrent && !busy;
    const onRowClick = rowClickable
      ? () => setSwitchTarget(o)
      : undefined;

    return (
      <tr
        key={o.id}
        className={`team-ws-tr ${isCurrent ? "current" : ""} ${
          rowClickable ? "clickable" : ""
        }`}
        onClick={onRowClick}
        role={rowClickable ? "button" : undefined}
        tabIndex={rowClickable ? 0 : undefined}
        onKeyDown={
          rowClickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSwitchTarget(o);
                }
              }
            : undefined
        }
      >
        <td>
          <span className="team-member-name">
            <span className="team-member-dot" aria-hidden="true" />
            <span>
              {o.name}
            </span>
            {isCurrent && <span className="team-ws-active">{t("active")}</span>}
          </span>
        </td>
        <td>
          {/* Workspaces tab intentionally uses a neutral role pill for
              every role (owner/admin/write/read) so the column reads as a
              calm label rather than a colour-coded status. */}
          <span className="team-role-badge read">{o.role}</span>
        </td>
        <td>{o.owner_user_id === myUserId ? t("team_owner_you") : (o.owner_name || "—")}</td>
        <td>{typeof o.member_count === "number" ? o.member_count : "—"}</td>
        <td>{typeof o.bot_count === "number" ? o.bot_count : "—"}</td>
        <td className="team-actions-cell" onClick={stop}>
          <div className="team-actions-row">
          <Tooltip
            title={canRename ? t("team_tooltip_rename") : t("team_tooltip_cant_rename")}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  setEditTarget(o);
                  setEditValue(o.name);
                  setError(null);
                }}
                disabled={busy || !canRename}
                sx={actionBtnSx}
              >
                <FontAwesomeIcon icon={faPen} style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={canLeave ? t("team_tooltip_leave") : t("team_tooltip_cant_leave")}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => handleLeave(o)}
                disabled={busy || !canLeave}
                sx={actionBtnSx}
              >
                <FontAwesomeIcon
                  icon={faRightFromBracket}
                  style={{ fontSize: 14 }}
                />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={canDelete ? t("team_tooltip_delete") : t("team_tooltip_cant_delete")}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => handleDelete(o)}
                disabled={busy || !canDelete}
                sx={actionBtnSx}
              >
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          </div>
        </td>
      </tr>
    );
  };

  // -----------------------------------------------------------------
  return (
    <div className="team-page">
      {/* Header reuses the shared `section-head` classes so the title /
          subtitle sizes and spacing match the Agents overview exactly. */}
      <header className="section-head">
        {activeTab === "members" ? (
          <>
            <h1 className="section-head-title">{t("team_members_title")}</h1>
            <p className="section-head-sub">
              <Trans
                i18nKey="team_members_subtitle"
                values={{ orgName }}
                components={{ 1: <strong /> }}
              />
            </p>
          </>
        ) : (
          <>
            <h1 className="section-head-title">{t("team_workspaces_title")}</h1>
            <p className="section-head-sub">
              <Trans
                i18nKey="team_workspaces_subtitle"
                values={{ orgName, role }}
                components={{ 1: <strong />, 3: <strong /> }}
              />
            </p>
          </>
        )}
      </header>

      {error && (
        <div className="team-error">
          <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
          <span>{error}</span>
          <button
            type="button"
            className="team-banner-close"
            aria-label={t("aria_dismiss")}
            onClick={() => setError(null)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      {success && (
        <div className="team-success">
          <FontAwesomeIcon icon={faCircleCheck} size="lg" />
          <span>{success}</span>
          <button
            type="button"
            className="team-banner-close"
            aria-label={t("aria_dismiss")}
            onClick={() => setSuccess(null)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {/* --------------------- Workspaces tab --------------------- */}
      {activeTab === "workspaces" && (
        <div className="team-card">
          <div className="team-card-header">
            <div>
              <h2 className="team-card-title">{t("team_your_workspaces_card_title")}</h2>
            </div>
            <Tooltip title={t("team_tooltip_create_workspace")}>
              <span>
                <Button
                  onClick={() => {
                    setEditValue("");
                    setError(null);
                    setShowCreateWs(true);
                  }}
                  disabled={busy}
                  sx={plusBtnSx}
                  startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 14 }} />}
                >
                  {t("team_workspace_btn")}
                </Button>
              </span>
            </Tooltip>
          </div>

          <div className="team-ws-list">
            {organizations.length === 0 ? (
              <div className="team-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            ) : (
              <div className="team-table-wrap">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th>{t("name")}</th>
                      <th>{t("team_th_your_role")}</th>
                      <th>{t("team_th_owner")}</th>
                      <th>{t("team_th_members")}</th>
                      <th>{t("team_th_agents")}</th>
                      <th>{t("skill_actions")}</th>
                    </tr>
                  </thead>
                  <tbody>{organizations.map(renderWorkspaceRow)}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --------------------- Team members tab --------------------- */}
      {activeTab === "members" && (
        <>
          {/* The active workspace is chosen from the sidebar workspace
              switcher; members below always reflect that current workspace. */}
          {!adminOrOwner ? (
            <div className="team-card">
              <p className="team-empty">
                {t("team_no_permission_empty")}
              </p>
            </div>
          ) : (
            <>
              {/* Members */}
              <div className="team-card">
                <div className="team-card-header">
                  <div>
                    <h2 className="team-card-title">{t("team_members_title")}</h2>
                  </div>
                  <Tooltip title={t("team_tooltip_invite")}>
                    <span>
                      <Button
                        onClick={() => {
                          setInviteEmail("");
                          setInviteRole("read");
                          setError(null);
                          setShowInvite(true);
                        }}
                        disabled={busy || inviting}
                        sx={plusBtnSx}
                        startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 14 }} />}
                      >
                        {t("team_invite_btn")}
                      </Button>
                    </span>
                  </Tooltip>
                </div>
                {loading ? (
                  <div className="team-loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </div>
                ) : members.length === 0 ? (
                  <p className="team-empty">{t("team_no_members_empty")}</p>
                ) : (
                  <div className="team-table-wrap">
                    <table className="team-table">
                      <thead>
                        <tr>
                          <th>{t("name")}</th>
                          <th>{t("email")}</th>
                          <th>{t("team_th_role")}</th>
                          <th>{t("skill_actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m) => {
                          const isOwnerRow = m.role === "owner";
                          const isSelf = m.user_id === myUserId;
                          return (
                            <tr key={m.id}>
                              <td>
                                <span className="team-member-name">
                                  <span
                                    className="team-member-dot"
                                    aria-hidden="true"
                                  />
                                  <span>
                                    {[m.first_name, m.last_name]
                                      .filter(Boolean)
                                      .join(" ") || "—"}
                                    {isSelf ? t("team_self_suffix") : ""}
                                  </span>
                                </span>
                              </td>
                              <td>{m.email}</td>
                              <td>
                                {isOwnerRow ? (
                                  <span className="team-role-badge owner">
                                    {t("team_role_badge_owner")}
                                  </span>
                                ) : (
                                  <span className={`team-role-badge ${m.role}`}>
                                    {m.role}
                                  </span>
                                )}
                              </td>
                              <td className="team-actions-cell">
                                {!isOwnerRow && !isSelf && (
                                  <div className="team-actions-row">
                                  <Tooltip title={t("team_tooltip_edit_role")}>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditMember(m);
                                        setEditMemberRole(m.role);
                                        setError(null);
                                      }}
                                      sx={actionBtnSx}
                                    >
                                      <FontAwesomeIcon
                                        icon={faPen}
                                        style={{ fontSize: 14 }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("team_tooltip_remove_member")}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleRemoveMember(m.user_id, m.email)
                                      }
                                      sx={actionBtnSx}
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        style={{ fontSize: 14 }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pending invitations */}
              <div className="team-card">
                <div className="team-card-header">
                  <h2 className="team-card-title">{t("team_pending_title")}</h2>
                </div>
                {loading ? (
                  <div className="team-loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </div>
                ) : pending.length === 0 ? (
                  <p className="team-empty">{t("team_no_pending_empty")}</p>
                ) : (
                  <div className="team-table-wrap">
                    <table className="team-table">
                      <thead>
                        <tr>
                          <th>{t("email")}</th>
                          <th>{t("team_th_role")}</th>
                          <th>{t("team_th_expires")}</th>
                          <th>{t("skill_actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map((inv) => (
                          <tr key={inv.id}>
                            <td>
                              <span className="team-member-name">
                                <span
                                  className="team-member-dot"
                                  aria-hidden="true"
                                />
                                <span>{inv.email}</span>
                              </span>
                            </td>
                            <td>
                              <span className={`team-role-badge ${inv.role}`}>
                                {inv.role}
                              </span>
                            </td>
                            <td>
                              {inv.expires_at
                                ? new Date(inv.expires_at).toLocaleDateString()
                                : "—"}
                            </td>
                            <td className="team-actions-cell">
                              <div className="team-actions-row">
                              <Tooltip title={t("team_tooltip_revoke")}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleRevokeInvite(inv.id, inv.email)
                                  }
                                  sx={actionBtnSx}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    style={{ fontSize: 14 }}
                                  />
                                </IconButton>
                              </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* --------------------- Switch-workspace confirmation --------------------- */}
      <Modal
        open={Boolean(switchTarget)}
        onClose={() => (busy ? null : setSwitchTarget(null))}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            padding: isMobile ? "20px" : "30px",
            width: isMobile ? "80%" : "90%",
            maxWidth: 400,
          }}
        >
          <div className="modal-title">
            <h2>{t("team_switch_modal_title")}</h2>
          </div>
          <p>
            <Trans
              i18nKey="team_switch_modal_body"
              values={{ name: switchTarget?.name }}
              components={{ 1: <strong /> }}
            />
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              onClick={() => setSwitchTarget(null)}
              disabled={busy}
              variant="outlined"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": {
                  borderColor: "#5e0fb3",
                  backgroundColor: "#f3eaff",
                },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                const target = switchTarget;
                setSwitchTarget(null);
                if (target) await handleSwitchOrg(target.id);
              }}
              disabled={busy}
              variant="contained"
              sx={{
                backgroundColor: "#5616ea",
                "&:hover": { backgroundColor: "#4a14d6" },
              }}
            >
              {t("team_switch_btn")}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* --------------------- Create-workspace modal --------------------- */}
      <Modal
        open={showCreateWs}
        onClose={() => {
          if (busy) return;
          setShowCreateWs(false);
          setEditValue("");
        }}
      >
        <Box
          component="form"
          onSubmit={handleCreate}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            padding: isMobile ? "20px" : "30px",
            width: isMobile ? "80%" : "90%",
            maxWidth: 400,
          }}
        >
          <div className="modal-title">
            <h2>{t("team_create_modal_title")}</h2>
          </div>
          <p>{t("team_create_modal_body")}</p>
          <TextField
            autoFocus
            fullWidth
            label={t("team_workspace_name_label")}
            placeholder={t("team_workspace_name_placeholder")}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            inputProps={{ maxLength: 80 }}
            required
            sx={{ ...inputSx, mb: 2 }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              type="button"
              onClick={() => {
                setShowCreateWs(false);
                setEditValue("");
              }}
              disabled={busy}
              variant="outlined"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": {
                  borderColor: "#5e0fb3",
                  backgroundColor: "#f3eaff",
                },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={busy || !editValue.trim()}
              variant="contained"
              sx={{
                backgroundColor: "#5616ea",
                "&:hover": { backgroundColor: "#4a14d6" },
              }}
            >
              {busy ? t("team_creating") : t("team_create_btn")}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* --------------------- Edit-workspace modal --------------------- */}
      <Modal
        open={Boolean(editTarget)}
        onClose={() => {
          if (busy) return;
          setEditTarget(null);
          setEditValue("");
        }}
      >
        <Box
          component="form"
          onSubmit={(e) => editTarget && handleRename(editTarget.id, e)}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            padding: isMobile ? "20px" : "30px",
            width: isMobile ? "80%" : "90%",
            maxWidth: 400,
          }}
        >
          <div className="modal-title">
            <h2>{t("team_edit_modal_title")}</h2>
          </div>
          <p>{t("team_edit_modal_body")}</p>
          <TextField
            autoFocus
            fullWidth
            label={t("team_workspace_name_label")}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            inputProps={{ maxLength: 80 }}
            required
            sx={{ ...inputSx, mb: 2 }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              type="button"
              onClick={() => {
                setEditTarget(null);
                setEditValue("");
              }}
              disabled={busy}
              variant="outlined"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": {
                  borderColor: "#5e0fb3",
                  backgroundColor: "#f3eaff",
                },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                busy ||
                !editValue.trim() ||
                editValue.trim() === (editTarget?.name || "")
              }
              variant="contained"
              sx={{
                backgroundColor: "#5616ea",
                "&:hover": { backgroundColor: "#4a14d6" },
              }}
            >
              {busy ? t("team_saving") : t("save")}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* --------------------- Edit-member-role modal --------------------- */}
      <Modal
        open={Boolean(editMember)}
        onClose={() => {
          if (busy) return;
          setEditMember(null);
        }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (editMember) {
              handleRoleChange(editMember.user_id, editMemberRole);
            }
          }}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            padding: isMobile ? "20px" : "30px",
            width: isMobile ? "80%" : "90%",
            maxWidth: 400,
          }}
        >
          <div className="modal-title">
            <h2>{t("team_edit_role_modal_title")}</h2>
          </div>
          <p>
            <Trans
              i18nKey="team_edit_role_modal_body"
              values={{
                name: editMember
                  ? [editMember.first_name, editMember.last_name]
                      .filter(Boolean)
                      .join(" ") || editMember.email
                  : "",
              }}
              components={{ 1: <strong /> }}
            />
          </p>
          <FormControl fullWidth sx={{ ...inputSx, mb: 2 }}>
            <InputLabel id="edit-member-role-label">{t("team_th_role")}</InputLabel>
            <Select
              labelId="edit-member-role-label"
              label={t("team_th_role")}
              value={editMemberRole}
              onChange={(e) => setEditMemberRole(e.target.value)}
            >
              {INVITE_ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {t(r.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              type="button"
              onClick={() => setEditMember(null)}
              disabled={busy}
              variant="outlined"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": {
                  borderColor: "#5e0fb3",
                  backgroundColor: "#f3eaff",
                },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                busy ||
                !editMember ||
                editMemberRole === (editMember?.role || "")
              }
              variant="contained"
              sx={{
                backgroundColor: "#5616ea",
                "&:hover": { backgroundColor: "#4a14d6" },
              }}
            >
              {busy ? t("team_saving") : t("save")}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* --------------------- Invite-team-member modal --------------------- */}
      <Modal
        open={showInvite}
        onClose={() => {
          if (inviting) return;
          setShowInvite(false);
        }}
      >
        <Box
          component="form"
          onSubmit={handleInvite}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            padding: isMobile ? "20px" : "30px",
            width: isMobile ? "80%" : "90%",
            maxWidth: 640,
          }}
        >
          <div className="modal-title" style={{ marginBottom: 20 }}>
            <h2>{t("team_invite_modal_title")}</h2>
          </div>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label={t("team_email_address_label")}
            placeholder={t("team_email_placeholder")}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
            sx={{ ...inputSx, mb: 3 }}
          />
          <FormControl fullWidth sx={{ ...inputSx, mb: 2.5 }}>
            <InputLabel>{t("team_th_role")}</InputLabel>
            <Select
              value={inviteRole}
              label={t("team_th_role")}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              {INVITE_ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {t(r.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: "20px",
              backgroundColor: "#5616ea25",
              borderRadius: "10px",
              borderLeft: "5px solid #5616ea",
              padding: "12px 15px",
              fontSize: "0.85rem",
              lineHeight: 1.45,
              color: "#3d3d4f",
            }}
          >
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ fontSize: 32, color: "#5616ea", flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#5616ea",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  padding: "2px 0 4px",
                }}
              >
                {t("team_role_explainer_heading")}
              </div>
              {[
                { label: t("team_role_read"), desc: t("team_role_read_desc") },
                { label: t("team_role_write"), desc: t("team_role_write_desc") },
                { label: t("team_role_admin"), desc: t("team_role_admin_desc") },
              ].map((row, i, arr) => (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom: i < arr.length - 1 ? "1px solid #c9b8ea" : "none",
                  }}
                >
                  <strong
                    style={{
                      color: "#5616ea",
                      flex: "0 0 70px",
                      textAlign: "left",
                    }}
                  >
                    {row.label}
                  </strong>
                  <span style={{ flex: 1 }}>{row.desc}</span>
                </div>
              ))}
            </div>
          </Box>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              type="button"
              onClick={() => setShowInvite(false)}
              disabled={inviting}
              variant="outlined"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": {
                  borderColor: "#5e0fb3",
                  backgroundColor: "#f3eaff",
                },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              variant="contained"
              sx={{
                backgroundColor: "#5616ea",
                "&:hover": { backgroundColor: "#4a14d6" },
              }}
            >
              {inviting ? t("team_sending") : t("team_send_invitation_btn")}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* --------------------- Shared destructive-action confirmation --------------------- */}
      <ConfirmationModal
        open={Boolean(confirmTarget)}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => {
          if (confirmTarget?.action) confirmTarget.action();
        }}
        itemName={confirmTarget?.itemName || t("team_confirm_default_item")}
        requireTextConfirmation={
          confirmTarget?.requireTextConfirmation || false
        }
      />
    </div>
  );
};

export default Team;
